import { useAlert } from '@genial/components';
import { BackendError, ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useToastCreator } from '../../hooks/useToastCreator';
import { useBlockListActions } from '../../store/blockList/actions';
import { searchTermState, blockListRetryUnlockCustomerSendState } from '../../store/blockList/atom';
import {
  CustomersListResponseNormalized,
  customersLockListQuery,
} from '../../store/blockList/selector';

import { ContainerDesktop } from './Desktop';
import { ContainerMobile } from './Mobile';

export const BlockList = () => {
  const [searchTerm, setSearchTerm] = useRecoilState(searchTermState);
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const { alert } = useAlert();

  const [isOpenWarning, setIsOpenWarning] = useState(true);

  const toggleOpen = () => {
    setIsOpenWarning(!isOpenWarning);
  };

  const { showToast, showToastFromError } = useToastCreator('unlock-toast');

  const {
    data: customersLockList,
    error: customersLockListError,
    loading: customersLockListLoading,
    refresher,
  } = useRecoilQuery(customersLockListQuery({ searchTerm }), {
    cachePolicy: 'no-cache',
  });

  const { retryUnlock } = useBlockListActions();

  const retryUnlockCustomerSendState = useRecoilValue(blockListRetryUnlockCustomerSendState);

  const [currentOptoutId, setCurrentOptoutId] = useState('');

  const sendRetryUnlockCustomer = ({ customerId, channel, optoutId }) => {
    if (customerId && channel) {
      retryUnlock({
        customerId,
        channel,
      });

      setCurrentOptoutId(optoutId);
    }
  };

  useEffect(() => {
    if (retryUnlockCustomerSendState.isSend) {
      refresher();

      if (!isMobile) {
        showToast(
          'success',
          'Tudo certo!',
          'Solicitação enviada com sucesso! O link enviado é de uso único e expirará após a primeira tentativa de acesso.',
          {
            duration: 3000,
          }
        );
      } else {
        alert(
          {
            icon: 'BackInTime',
            description:
              'Solicitação enviada com sucesso! O link enviado é de uso único e expirará após a primeira tentativa de acesso.',
          },
          () => {}
        );
      }
    } else if (retryUnlockCustomerSendState.isError) {
      if (!isMobile) {
        showToastFromError({
          errorMessage: 'Ocorreu um erro!',
          errorSuggest: 'Por favor, tente novamente mais tarde.',
        });
      } else {
        alert(
          {
            icon: 'BackInTime',
            description: 'Ocorreu um erro! Por favor, tente novamente mais tarde.',
          },
          () => {}
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryUnlockCustomerSendState]);

  const handleRefresh = useCallback(() => {
    refresher();
  }, [refresher]);

  const [filtered, setFiltered] = useState(false);

  useEffect(() => {
    if (customersLockListError) {
      if (instanceOfErrorListResponse(customersLockListError)) {
        (customersLockListError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          !isMobile
            ? showToastFromError(errorItem)
            : alert(
                {
                  icon: 'Warning',
                  iconColor: 'feedback.attention.default',
                  description: errorItem.errorMessage,
                },
                () => {}
              )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersLockListError]);

  const handleClearSearchTerm = React.useCallback(() => {
    setSearchTerm('');
    setFiltered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responsiveContainer = useBreakpointValue({
    base: (
      <ContainerMobile
        currentOptoutId={currentOptoutId}
        sendRetryUnlockCustomer={sendRetryUnlockCustomer}
        isMobile={true}
        searchTerm={searchTerm}
        onSetSearchTerm={setSearchTerm}
        onClearSearchTerm={handleClearSearchTerm}
        blockList={customersLockList?.data as unknown as CustomersListResponseNormalized[]}
        blockListLoading={customersLockListLoading}
        blockListError={customersLockListError as unknown as BackendError}
        blockListRetryLoading={
          retryUnlockCustomerSendState.isLoading || retryUnlockCustomerSendState.isSend
        }
        onRefresh={handleRefresh}
        filtered={filtered}
        toggleOpen={toggleOpen}
        isOpenWarning={isOpenWarning}
      />
    ),
    sm: (
      <ContainerDesktop
        currentOptoutId={currentOptoutId}
        sendRetryUnlockCustomer={sendRetryUnlockCustomer}
        isMobile={false}
        searchTerm={searchTerm}
        onSetSearchTerm={setSearchTerm}
        onClearSearchTerm={handleClearSearchTerm}
        blockList={customersLockList?.data as unknown as CustomersListResponseNormalized[]}
        blockListLoading={customersLockListLoading}
        blockListError={customersLockListError as unknown as BackendError}
        blockListRetryLoading={
          retryUnlockCustomerSendState.isLoading || retryUnlockCustomerSendState.isSend
        }
        onRefresh={handleRefresh}
        filtered={filtered}
        toggleOpen={toggleOpen}
        isOpenWarning={isOpenWarning}
      />
    ),
  });

  return responsiveContainer;
};
